/**
 * TenantsPage — Akhile's workspace and seat administration.
 *
 * Feature: Multi-tenancy (v0.13.0)
 *
 * Platform administrators only. This is where a client account is created, its purchased seat
 * count is set, and its first Owner is invited — after which that Owner manages their own team
 * from /admin/users, and Akhile does not need to be involved.
 *
 * The seat counter is the commercially important number on this page: `seats_used` counts
 * memberships that are invited or active AND seat_counted, so an invitation that has not been
 * accepted yet still holds its seat. Akhile staff added to a client workspace are recorded
 * with seat_counted = false, so they never appear against a client's paid total.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Building2, Loader2, Plus, UserPlus } from 'lucide-react';

import Button from '../../components/Button';
import Card from '../../components/Card';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Badge from '../../components/Badge';
import EmptyStateCard from '../../components/EmptyStateCard';
import * as tenantAPI from '../../api/tenants';
import { TENANT_ROLES } from '../../utils/permissions';
import type { TenantRole, TenantWithSeats } from '../../types/auth';
import showToast from '../../utils/toast';

const ROLE_OPTIONS = TENANT_ROLES.map((r) => ({ value: r, label: r }));

/** Derive a URL-safe slug so the operator does not have to hand-craft one. */
const slugify = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

const TenantsPage: React.FC = () => {
  const [tenants, setTenants] = useState<TenantWithSeats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [busyTenantId, setBusyTenantId] = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [newSeats, setNewSeats] = useState('5');
  const [slugEdited, setSlugEdited] = useState(false);

  const [inviteFor, setInviteFor] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<TenantRole>('Owner');
  const [inviteSeatCounted, setInviteSeatCounted] = useState(true);

  const [confirmSuspendId, setConfirmSuspendId] = useState<string | null>(null);

  /**
   * What the operator has typed into each row's seats field, keyed by tenant id. Absent means
   * "showing the server's number". Held here rather than in the input so a refused PATCH can
   * put the server's value back — an uncontrolled field would leave the rejected number on
   * screen looking as though it had been accepted.
   */
  const [seatsDraft, setSeatsDraft] = useState<Record<string, string>>({});

  /**
   * `silent` refreshes the list without collapsing the page back to its spinner. Mutations use
   * it so the per-row busy indicator is the only thing that moves: a full blank would throw
   * away scroll position.
   *
   * Either way the drafts are dropped, so every seats field goes back to reading what the
   * server just returned.
   */
  const load = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      setTenants(await tenantAPI.listTenants());
      setSeatsDraft({});
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load workspaces');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleNameChange = (value: string) => {
    setNewName(value);
    if (!slugEdited) setNewSlug(slugify(value));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      await tenantAPI.createTenant({
        name: newName.trim(),
        slug: newSlug.trim(),
        seats_purchased: Number(newSeats) || 0,
      });
      showToast.success(`Workspace "${newName.trim()}" created`);
      setNewName('');
      setNewSlug('');
      setNewSeats('5');
      setSlugEdited(false);
      await load({ silent: true });
    } catch (e) {
      showToast.error(e instanceof Error ? e.message : 'Could not create workspace');
    } finally {
      setCreating(false);
    }
  };

  /** Put a row's seats field back to what the server says it is. */
  const resetSeatsDraft = (tenant: TenantWithSeats) =>
    setSeatsDraft((draft) => ({ ...draft, [tenant.id]: String(tenant.seats_purchased) }));

  const handleSeatsCommit = async (tenant: TenantWithSeats, raw: string) => {
    const seats = Number(raw);

    // A cleared field is not "zero seats". `Number('')` is 0, so tabbing out of an empty input
    // used to cancel the client's entire allocation without so much as a prompt.
    if (raw.trim() === '' || !Number.isInteger(seats) || seats < 0) {
      resetSeatsDraft(tenant);
      return;
    }

    if (seats === tenant.seats_purchased) {
      resetSeatsDraft(tenant);
      return;
    }

    setBusyTenantId(tenant.id);
    try {
      await tenantAPI.updateTenant(tenant.id, { seats_purchased: seats });
      showToast.success(`${tenant.name}: seats set to ${seats}`);
      await load({ silent: true });
    } catch (e) {
      showToast.error(e instanceof Error ? e.message : 'Could not update seats');
      resetSeatsDraft(tenant);
    } finally {
      setBusyTenantId(null);
    }
  };

  const handleToggleStatus = async (tenant: TenantWithSeats) => {
    const next = tenant.status === 'active' ? 'suspended' : 'active';
    setConfirmSuspendId(null);
    setBusyTenantId(tenant.id);
    try {
      await tenantAPI.updateTenant(tenant.id, { status: next });
      showToast.success(`${tenant.name} ${next === 'suspended' ? 'suspended' : 'reactivated'}`);
      await load({ silent: true });
    } catch (e) {
      showToast.error(e instanceof Error ? e.message : 'Could not change status');
    } finally {
      setBusyTenantId(null);
    }
  };

  /**
   * Open (or close) a row's invite form, always on a clean slate.
   *
   * The form's fields are page-level state shared by every row, so without this reset a failed
   * invitation left "Akhile staff" ticked and carried it into the next workspace opened —
   * inviting a paying client's user with seat_counted = false, off their seat count entirely.
   */
  const toggleInviteFor = (tenantId: string) => {
    setInviteFor((current) => (current === tenantId ? null : tenantId));
    setInviteEmail('');
    setInviteRole('Owner');
    setInviteSeatCounted(true);
  };

  const handleInvite = async (e: React.FormEvent, tenantId: string) => {
    e.preventDefault();
    setBusyTenantId(tenantId);
    try {
      const membership = await tenantAPI.addTenantMember(
        tenantId, inviteEmail.trim(), inviteRole, inviteSeatCounted
      );
      // Someone who already has a login has nothing to accept, so the backend makes them
      // active at once and no email invitation goes out. Reporting "invitation sent" would
      // leave the admin waiting for an acceptance that is never coming. Read the status the
      // server actually returned rather than guessing whether the login existed.
      showToast.success(
        membership.status === 'active'
          ? `${inviteEmail.trim()} now has access to this workspace`
          : `Invitation sent to ${inviteEmail.trim()}`
      );
      setInviteEmail('');
      setInviteFor(null);
      setInviteSeatCounted(true);
      // An invitation holds a seat, so this tenant's counters moved — but only this one's.
      // Re-read the single row rather than the whole list, which costs a query per tenant.
      //
      // Kept out of the invite's own failure domain: the invitation has already committed by
      // this point, so a failed re-read must not report the invitation as failed. The stale
      // counter corrects itself on the next load.
      try {
        const refreshed = await tenantAPI.getTenant(tenantId);
        setTenants((prev) => prev.map((t) => (t.id === tenantId ? refreshed : t)));
      } catch {
        /* counters will catch up on the next refresh */
      }
    } catch (e) {
      showToast.error(e instanceof Error ? e.message : 'Could not send invitation');
    } finally {
      setBusyTenantId(null);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" aria-label="Loading workspaces" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Building2 className="w-6 h-6" aria-hidden="true" />
          Workspaces &amp; Seats
        </h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Each workspace is one client, and its data is invisible to every other workspace.
          Create one, set the seats they have bought, then invite their first Owner — they
          manage their own team from there.
        </p>
      </div>

      {error && (
        <Card>
          <Card.Body>
            <p className="text-red-600 dark:text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" aria-hidden="true" />
              {error}
            </p>
          </Card.Body>
        </Card>
      )}

      {/* Create */}
      <Card>
        <Card.Header>
          <h2 className="text-lg font-semibold">New workspace</h2>
        </Card.Header>
        <Card.Body>
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <Input
              label="Client name"
              value={newName}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Acme Municipality"
              required
            />
            <Input
              label="Slug"
              value={newSlug}
              onChange={(e) => {
                setSlugEdited(true);
                setNewSlug(e.target.value);
              }}
              placeholder="acme-municipality"
              helperText="Lowercase letters, numbers and hyphens"
              required
            />
            <Input
              label="Seats purchased"
              type="number"
              min={0}
              value={newSeats}
              onChange={(e) => setNewSeats(e.target.value)}
              required
            />
            <Button type="submit" variant="primary" isLoading={creating} leftIcon={Plus}>
              Create
            </Button>
          </form>
        </Card.Body>
      </Card>

      {/* List */}
      <div className="space-y-4">
        {tenants.map((tenant) => {
          const busy = busyTenantId === tenant.id;
          const overSubscribed = tenant.seats_used > tenant.seats_purchased;
          return (
            <Card key={tenant.id}>
              <Card.Body>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {tenant.name}
                      </h3>
                      <code className="text-xs text-gray-500 dark:text-gray-400">{tenant.slug}</code>
                      {tenant.is_platform_tenant && <Badge variant="info">Akhile</Badge>}
                      {tenant.status === 'suspended' && <Badge variant="warning">Suspended</Badge>}
                      {overSubscribed && <Badge variant="danger">Over seat limit</Badge>}
                    </div>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      <strong>{tenant.seats_used}</strong> of <strong>{tenant.seats_purchased}</strong>{' '}
                      seats in use
                      {tenant.seats_available === 0 && !overSubscribed && (
                        <span className="text-amber-600 dark:text-amber-400">
                          {' '}— no seats free, further invitations will be refused
                        </span>
                      )}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <Input
                      aria-label={`Seats purchased for ${tenant.name}`}
                      type="number"
                      min={0}
                      value={seatsDraft[tenant.id] ?? String(tenant.seats_purchased)}
                      onChange={(e) =>
                        setSeatsDraft((draft) => ({ ...draft, [tenant.id]: e.target.value }))
                      }
                      onBlur={(e) => handleSeatsCommit(tenant, e.target.value)}
                      className="w-24"
                    />
                    <Button variant="outline" disabled={busy} onClick={() => toggleInviteFor(tenant.id)}>
                      <UserPlus className="w-4 h-4" />
                      Invite
                    </Button>
                    {!tenant.is_platform_tenant &&
                      (tenant.status === 'active' ? (
                        /* Suspending locks a whole client out of the product, so it asks first.
                           Reactivating restores access and needs no such guard. */
                        confirmSuspendId === tenant.id ? (
                          <span className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            Suspend {tenant.name}?
                            <Button
                              variant="danger"
                              disabled={busy}
                              onClick={() => handleToggleStatus(tenant)}
                            >
                              Suspend
                            </Button>
                            <Button variant="outline" onClick={() => setConfirmSuspendId(null)}>
                              Cancel
                            </Button>
                          </span>
                        ) : (
                          <Button
                            variant="outline"
                            disabled={busy}
                            onClick={() => setConfirmSuspendId(tenant.id)}
                          >
                            Suspend
                          </Button>
                        )
                      ) : (
                        <Button
                          variant="outline"
                          disabled={busy}
                          onClick={() => handleToggleStatus(tenant)}
                        >
                          Reactivate
                        </Button>
                      ))}
                  </div>
                </div>

                {inviteFor === tenant.id && (
                  <form
                    onSubmit={(e) => handleInvite(e, tenant.id)}
                    className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-4 gap-4 items-end"
                  >
                    <Input
                      label="Email"
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="owner@client.example"
                      required
                    />
                    <Select
                      label="Role in workspace"
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value as TenantRole)}
                      options={ROLE_OPTIONS}
                    />
                    <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={!inviteSeatCounted}
                        onChange={(e) => setInviteSeatCounted(!e.target.checked)}
                      />
                      <span>
                        Akhile staff
                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                          Does not use one of the client&apos;s paid seats
                        </span>
                      </span>
                    </label>
                    <Button type="submit" variant="primary" isLoading={busy} leftIcon={UserPlus}>
                      Send invitation
                    </Button>
                  </form>
                )}
              </Card.Body>
            </Card>
          );
        })}

        {tenants.length === 0 && (
          <EmptyStateCard
            icon="🏢"
            title="No workspaces yet"
            message="Create one above to onboard your first client."
          />
        )}
      </div>
    </div>
  );
};

export default TenantsPage;
